/**
 * PDF Suite — Core Type Definitions
 *
 * Types for the PDF document model: single-page templates composed into
 * multi-page PDFs via pdf-lib. Follows Option A architecture (composition
 * from single-page templates rather than multi-page editor).
 */

// ─── Document Kind ──────────────────────────────────────────────────

/**
 * The kind of PDF document being composed.
 * Each kind may have different default templates and layout options.
 */
export type DocumentKind = 'report' | 'proposal' | 'contract' | 'invoice' | 'nda' | 'freelance_contract' | 'employment_agreement' | 'scope_of_work' | 'quote' | 'letter_of_intent' | 'custom';

// ─── Page Size ──────────────────────────────────────────────────────

export interface PdfPageSize {
  /** Width in points (72 points = 1 inch) */
  width: number;
  /** Height in points (72 points = 1 inch) */
  height: number;
  /** Human-readable label */
  label: string;
}

export const PDF_PAGE_SIZES: Record<string, PdfPageSize> = {
  letter: { width: 612, height: 792, label: 'US Letter' },
  a4: { width: 595.28, height: 841.89, label: 'A4' },
  legal: { width: 612, height: 1008, label: 'US Legal' },
  tabloid: { width: 792, height: 1224, label: 'Tabloid' },
} as const;

export type PdfPageSizeKey = keyof typeof PDF_PAGE_SIZES;

// ─── Page Margins ───────────────────────────────────────────────────

export interface PdfMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const PDF_DEFAULT_MARGINS: PdfMargins = {
  top: 28.8,   // 0.4in
  right: 28.8,
  bottom: 28.8,
  left: 28.8,
};

// ─── Document Page Reference ────────────────────────────────────────

/**
 * A reference to a single page within a composed document.
 * Each page is a rendered SnapLink (single-canvas template).
 */
export interface DocumentPageRef {
  /** Unique identifier for this page within the document */
  id: string;
  /** SnapLink slug that renders this page's content */
  slug: string;
  /** Optional inline dimensions for layered page rendering. */
  width?: number;
  height?: number;
  /** Optional inline layers for documents that do not resolve from a SnapLink slug. */
  layers?: Record<string, unknown>[];
  /** Optional template ID for direct template references */
  template_id?: string;
  /** Variable overrides for this page's SnapLink */
  variables?: Record<string, string>;
  /** Page format for rendering (png or jpg) */
  render_format?: 'png' | 'jpg';
  /** Render width */
  render_width?: number;
  /** Render height */
  render_height?: number;
  /** Page label shown in the composer UI */
  label?: string;
  /** Page sort order */
  sort_order: number;
}

// ─── Document Definition ────────────────────────────────────────────

/**
 * Full definition of a composed PDF document.
 * Stored in report_definitions.definition jsonb column.
 */
/**
 * A document-level layer rendered across/over pages (e.g. FlowText).
 */
export interface DocumentLayerEntry {
  /** Unique id for this layer */
  id: string;
  /** Layer type — currently only 'flowText' */
  type: 'flowText';
  /** Layer-specific data (blocks, margins, font settings, etc.) */
  data: Record<string, unknown>;
}

export interface DocumentDefinition {
  /** Schema version for forward compatibility */
  schema_version: 1;
  /** Kind of document */
  doc_kind: DocumentKind;
  /** Document title */
  title: string;
  /** Optional description */
  description?: string;
  /** Page size for the final PDF */
  page_size: PdfPageSizeKey;
  /** Margins for the final PDF */
  margins: PdfMargins;
  /** Ordered list of page references */
  pages: DocumentPageRef[];
  /** Document-level layers rendered across pages (FlowText, etc.) */
  document_layers?: DocumentLayerEntry[];
  /** Optional metadata */
  metadata?: Record<string, unknown>;
}

// ─── Document Run ───────────────────────────────────────────────────

export type DocumentRunStatus = 'pending' | 'rendering' | 'complete' | 'failed';

/**
 * A single execution/generation of a document definition.
 * Maps to report_runs table.
 */
export interface DocumentRun {
  id: string;
  definition_id: string;
  status: DocumentRunStatus;
  /** R2 key where the generated PDF is stored */
  pdf_key?: string;
  /** Public URL for the generated PDF (if R2_PUBLIC_BASE is set) */
  pdf_url?: string;
  /** Number of pages in the generated PDF */
  page_count?: number;
  /** Error message if status is 'failed' */
  error?: string;
  /** When the run started */
  started_at: string;
  /** When the run completed */
  completed_at?: string;
}

// ─── PDF Export Options (client-side) ───────────────────────────────

/**
 * Options for exporting a single template canvas as a PDF page.
 * Used by the editor's export flow.
 */
export interface PdfExportOptions {
  /** Page size */
  pageSize: PdfPageSizeKey;
  /** Page orientation */
  orientation: 'portrait' | 'landscape';
  /** Margins */
  margins: PdfMargins;
  /** Whether to fit canvas content to page or use actual dimensions */
  fitToPage: boolean;
  /** Quality for embedded images (0-1) */
  imageQuality?: number;
}

export const DEFAULT_PDF_EXPORT_OPTIONS: PdfExportOptions = {
  pageSize: 'letter',
  orientation: 'portrait',
  margins: PDF_DEFAULT_MARGINS,
  fitToPage: true,
  imageQuality: 0.92,
};

// ─── Template Category Metadata ─────────────────────────────────────

/**
 * Category metadata for template packs, stored as part of template data.
 * Enables grouping templates into report/proposal/contract packs.
 */
export type TemplatePdfCategory = 'report' | 'proposal' | 'contract' | 'invoice' | 'presentation';

export interface TemplatePdfMetadata {
  /** Whether this template is designed for PDF document composition */
  pdf_enabled: boolean;
  /** Category for grouping in the document composer */
  category?: TemplatePdfCategory;
  /** Pack identifier — templates sharing the same pack_id belong together */
  pack_id?: string;
  /** Sort order within the pack */
  pack_sort_order?: number;
  /** Suggested page size when used as a PDF page */
  suggested_page_size?: PdfPageSizeKey;
}

// ─── Watermark Options ──────────────────────────────────────────────

export interface WatermarkOptions {
  /** The watermark text to render */
  text: string;
  /** Font size in points (default 48) */
  fontSize?: number;
  /** Opacity from 0-1 (default 0.15) */
  opacity?: number;
  /** Rotation in degrees (default -45) */
  rotation?: number;
  /** Hex color (default #888888) */
  color?: string;
}

// ─── Header / Footer Options ────────────────────────────────────────

export type TextAlign = 'left' | 'center' | 'right';

export interface HeaderFooterOptions {
  /** Header text. Use {{page}} for current page and {{total}} for total pages. */
  headerText?: string;
  /** Header alignment (default center) */
  headerAlign?: TextAlign;
  /** Footer text. Use {{page}} for current page and {{total}} for total pages. */
  footerText?: string;
  /** Footer alignment (default center) */
  footerAlign?: TextAlign;
  /** Font size in points (default 9) */
  fontSize?: number;
  /** Hex color (default #666666) */
  color?: string;
}

// ─── Enhanced Export Options ─────────────────────────────────────────

export interface PdfPostProcessOptions {
  /** Optional watermark to apply */
  watermark?: WatermarkOptions;
  /** Optional header/footer to apply */
  headerFooter?: HeaderFooterOptions;
  /** Document metadata */
  metadata?: {
    title?: string;
    author?: string;
    subject?: string;
    keywords?: string[];
  };
}
